import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../hooks/useFetch';
import Page from '../../components/Page';
import ActionBar from '../../components/ActionBar';
import Sheet from '../../components/Sheet';
import PageTitle from '../../components/PageTitle';
import DataTable from '../../components/DataTable';
import Box from '@mui/material/Box';
import useIsMobile from '../../hooks/useIsMobile';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import { capitalizeFirstLetters } from '../../utils';

export default function Vendors(props) {
    const { t } = useTranslation();
    const { setAuth, vendors, setVendors } = props;
    const [showAddVendor, setShowAddVendor] = useState(false);
    const [showDeleteVendor, setShowDeleteVendor] = useState(false);
    // null, not 0: row ids are array indices, so 0 is the first vendor rather than
    // "nothing selected". Conflating the two left the first vendor in the list
    // permanently undeletable.
    const [targetVendorID, setTargetVendorID] = useState(null);
    const [newVendorName, setNewVendorName] = useState('');
    const { fetchResource, showAlert, setShowAlert, alertMessage, setAlertMessage } = useFetch({ setAuth });
    const isMobile = useIsMobile();

    const columns = [{
        field: 'vendor',
        headerName: t('vendors.columns.name'),
        minWidth: 150,
        flex: 1
    }];

    const rows = (vendors || []).map((vendor, id) => ({ id, vendor }));

    // One column does not need a grid around it: headers, a footer and a scroll
    // container for a list of names.
    const renderVendorCard = row => (
        <Typography sx={{ fontWeight: 500 }} noWrap>{row.vendor}</Typography>
    );

    function handleSelectChange(e) {
      setTargetVendorID(e.length ? e[0] : null)
    }

    async function handleAddVendor() {
      if (!newVendorName) return;

      try {
        const { vendors, error } = await fetchResource({ resource: 'vendors/add', body: { vendor: capitalizeFirstLetters(newVendorName).trim() }, blocking: true });
        if (error) throw new Error(error);
        setAlertMessage({ message: t('vendors.addSuccess'), type: 'success' });
        setShowAlert(true);
        setShowAddVendor(false);
        setVendors(vendors);
        setNewVendorName('');
      } catch (error) {
          alert(t('vendors.addVendorError', { message: error.message }));
      }
    }

    async function handleDeleteVendor() {
      try {
        const { vendors:updatedVendors, error } = await fetchResource({ resource: 'vendors/delete', body: { vendor: vendors[targetVendorID] }, blocking: true });
        if (error) throw new Error(error);
        setAlertMessage({ message: t('vendors.deleteSuccess'), type: 'success' });
        setShowAlert(true);
        setShowDeleteVendor(false);
        setVendors(updatedVendors);
        setNewVendorName('');
      } catch (error) {
          alert(t('vendors.addVendorError', { message: error.message }));
      }
    }

    const ConfirmDeleteVendor = () => {
      return (
        <Sheet
            open={showDeleteVendor}
            onClose={() => setShowDeleteVendor(false)}
            actions={
                /* Cancel was outlined red and Delete filled red, so the two read as
                   the same kind of thing. Only one of them destroys a vendor. */
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: () => setShowDeleteVendor(false) }}
                    confirm={{ label: t('common.delete'), onClick: () => handleDeleteVendor(), tone: 'danger' }}
                />
            }
        >
            <Typography variant='h6'>{t('vendors.confirmDeleteMessage', { vendor: vendors[targetVendorID] })}</Typography>
        </Sheet>
      )
    }

    const AddVendor = () => {
      return (
        <Sheet
            open={showAddVendor}
            onClose={() => setShowAddVendor(false)}
            title={t('vendors.enterNewName')}
            actions={
                <ActionBar
                    cancel={{
                        label: t('common.cancel'),
                        onClick: () => {
                            setNewVendorName('')
                            setShowAddVendor(false)
                        }
                    }}
                    confirm={{ label: t('vendors.addVendor'), onClick: handleAddVendor, type: 'submit' }}
                />
            }
        >
            <TextField
                fullWidth
                id='vendorName'
                label={t('vendors.columns.name')}
                value={newVendorName}
                onChange={e => setNewVendorName(e.target.value)}
            />
        </Sheet>
      )
    }

    const AlertMessage = () => <Alert className='alert' severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>

    return (<>
      {showDeleteVendor && ConfirmDeleteVendor()}
      {showAddVendor && AddVendor()}
      {showAlert && AlertMessage()}
      <Page>
          <PageTitle>{t('vendors.title')}</PageTitle>
          <DataTable
              columns={columns}
              rows={rows}
              handleSelectChange={handleSelectChange}
              renderCard={renderVendorCard}
              // Controlled, so the card list shows the vendor Delete will act on.
              // null is "nobody" here; 0 is the first vendor.
              selectionModel={targetVendorID === null ? [] : [targetVendorID]}
              id='vendors-list'
          />
          {/* Stacked and full width on a phone, primary first, the destructive
              one furthest from the thumb. */}
          <Box
              sx={{
                  flex: 'none',
                  display: 'flex',
                  gap: 1.5,
                  mt: 1,
                  flexDirection: isMobile ? 'column' : 'row-reverse',
                  justifyContent: 'space-between'
              }}
          >
              <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddVendor(true)}
                  fullWidth={isMobile}
              >
                  {t('vendors.newVendor')}
              </Button>
              {/* Outlined and red: it does the opposite of "New vendor" and should
                  not carry the same weight, let alone the accent. */}
              <Button
                  variant='outlined'
                  color='error'
                  startIcon={<DeleteIcon />}
                  onClick={() => setShowDeleteVendor(true) }
                  disabled={targetVendorID === null}
                  fullWidth={isMobile}
              >
                  {t('common.delete')}
              </Button>
          </Box>
      </Page>
    </>)
}
